import { useSelector, useDispatch } from 'react-redux';
import { useEffect, useState } from 'react';
import { DESCENDING } from 'constants/sortDirections';
import { PaginationFooter } from 'ui/paginationFooter';
import { Table, sortRows } from 'ui/table';
import { Skeleton } from 'ui/skeleton';
import { InputSearch } from 'ui/inputs';
import { Tooltip } from 'ui/tooltip';
import TableHeader from 'ui/tableHeader/TableHeader';
import TableRows from 'ui/tableRows/TableRows';
import { useDebouncedEffect } from 'utils/hooks/useDebouncedEffect';
import { PrimaryButton, SecondaryButton } from 'ui/button';
import { AddNew16, Delete16 } from 'ui/icons/aurora';
import { PageHeader } from 'ui/pageHeader';
import { fetchMetricsRequest } from 'modules/metrics';
import { DeleteReportModal } from 'modules/deleteReportModal';
import * as reportActions from 'modules/report/actions';
import { PROJECT_PERMISSIONS } from 'utils/permissions';
import { selectDoesUserHaveRbacPermission } from 'core/userContext/selectors';
import { selectFormattedHighLevelMetrics, selectHighLevelMetrics } from 'modules/report/selectors';
import {
  selectReportsTableTotal,
  selectReportTableData,
  selectDataSource,
  selectReportsTableIsLoading,
  selectReportsTableConfig,
} from '../selectors';
import { fetchFilteredReportTable, createNewReport as createNewReportRequest } from '../actions';
import { generateRowColumns, generateHeaderColumns } from '../utils';
import { TableHeaderContainer, Container } from '../ReportsTable.styled';
import { CreateReportModal } from './CreateReportModal';

const SELECTED_TYPE_IDS = {
  Campaign: [],
  Workflow: [],
};

export default () => {
  const dispatch = useDispatch();
  const [createReportModalIsOpen, setCreateReportModalIsOpen] = useState(false);
  const [deleteReportModalIsOpen, setDeleteReportModalIsOpen] = useState(false);
  const [sortedBy, setSortedBy] = useState('id');
  const [filter, setFilter] = useState('');
  const [sortDirection, setSortDirection] = useState(DESCENDING);
  const [selectedReportIds, setSelectedReportIds] = useState([]);
  const [deleteReportData, setDeleteReportData] = useState({});

  const canManageReports = useSelector(selectDoesUserHaveRbacPermission)(
    PROJECT_PERMISSIONS.ManageReports,
  );

  const data = useSelector(selectReportTableData);
  const highLevelMetrics = useSelector(selectFormattedHighLevelMetrics).filter(
    (metric) => metric.value !== 'OpenRateSegmentAnalysis',
  );
  const highLevelMetricsData = useSelector(selectHighLevelMetrics).filter(
    (metric) => metric.enumName !== 'OpenRateSegmentAnalysis',
  );
  const isLoading = useSelector(selectReportsTableIsLoading);
  const reportDataSource = useSelector(selectDataSource);
  const total = useSelector(selectReportsTableTotal);
  const tableConfig = useSelector(selectReportsTableConfig);

  const createNewReport = (report) => dispatch(createNewReportRequest(report));

  const sortedRows = sortRows(data, sortedBy, sortDirection);

  const deleteSelectedReports = (reportId, reportName) => {
    setSelectedReportIds([reportId]);
    setDeleteReportData({ reportName, reportId });
    setDeleteReportModalIsOpen(true);
  };

  const closeDeleteModal = () => {
    setDeleteReportData({});
    setDeleteReportModalIsOpen(false);
  };

  const fetchData = (paginationChanges, filterChange) => {
    const config = {
      ...tableConfig,
      orderBy: paginationChanges?.orderBy || tableConfig.orderBy,
      filter: filterChange !== undefined ? filterChange : tableConfig.filter,
      page: paginationChanges?.page || 1,
      pageSize: paginationChanges?.pageSize || tableConfig.pageSize,
      sortDesc: sortDirection === DESCENDING,
    };

    dispatch(fetchFilteredReportTable(config));
    dispatch(
      reportActions.fetchReportingTypeTable('Campaign', {
        tableConfig: config,
        selectedTypeIds: SELECTED_TYPE_IDS,
      }),
    );
    dispatch(fetchMetricsRequest());
  };

  useEffect(() => {
    // despite that the table header for the column "selectedMetrics" is set to not be sortable,
    // clicking on the name will still set off the function.
    // this then causes an undesired UX effect of a permanent load screen.
    // TODO: fix ui/table accordingly so that a non-sortable item won't fire off the onClick callback
    const orderBy = sortedBy === 'selectedMetrics' ? undefined : sortedBy;
    fetchData({ orderBy, sortDirection });
  }, [sortedBy, sortDirection]);

  useDebouncedEffect(
    () => {
      fetchData(undefined, filter);
    },
    [filter],
    { debounceTimeout: 600, skipEffectOnMount: true },
  );

  return (
    <Container>
      <PageHeader
        pageActions={
          <Tooltip
            isEnabled={!canManageReports}
            tooltipContent="You do not have permissions to create and manage reports. A project administrator
              could change this."
          >
            <PrimaryButton
              isDisabled={!canManageReports}
              onClick={() => setCreateReportModalIsOpen(true)}
            >
              <AddNew16 />
              Create a new report
            </PrimaryButton>
          </Tooltip>
        }
      >
        Reports
      </PageHeader>
      {isLoading ? (
        <Skeleton height={500} />
      ) : (
        <>
          <TableHeaderContainer>
            <SecondaryButton
              isDisabled={selectedReportIds.length === 0}
              onClick={() => setDeleteReportModalIsOpen(true)}
            >
              <Delete16 />
              Delete
            </SecondaryButton>
            <InputSearch
              handleClear={() => setFilter('')}
              id="filter-reports"
              onChange={setFilter}
              placeholder={'Filter...'}
              value={filter}
            />
          </TableHeaderContainer>
          <Table>
            <TableHeader
              columns={generateHeaderColumns(setSelectedReportIds, sortedRows, selectedReportIds)}
              sortedBy={sortedBy}
              setSortedBy={setSortedBy}
              sortDirection={sortDirection}
              setSortDirection={setSortDirection}
            />
            <TableRows
              rows={sortedRows}
              columns={generateRowColumns(
                setSelectedReportIds,
                selectedReportIds,
                deleteSelectedReports,
              )}
            />
          </Table>
          <PaginationFooter
            onUpdate={fetchData}
            pageSizes={[10, 20, 50]}
            currentPage={tableConfig.page}
            itemsPerPage={tableConfig.pageSize}
            numItems={total}
          />
        </>
      )}
      {createReportModalIsOpen && (
        <CreateReportModal
          closeCreateReportModal={() => setCreateReportModalIsOpen(false)}
          createNewReport={createNewReport}
          createReportModalIsOpen={createReportModalIsOpen}
          highLevelMetrics={highLevelMetrics}
          highLevelMetricsData={highLevelMetricsData}
          reportDataSource={reportDataSource}
        />
      )}
      {deleteReportModalIsOpen && (
        <DeleteReportModal
          deleteReportData={deleteReportData}
          isOpen={deleteReportModalIsOpen}
          onClose={closeDeleteModal}
          redirectOnComplete={false}
          selectedReportIds={selectedReportIds}
        />
      )}
    </Container>
  );
};
